// This code is essentially a copy of https://github.com/fedorovsky/draft-js-link-detection-plugin
// I tweaked 2 things: 1) use the tlds package for the top-level domain list
// used by linkify and 2) allow link detection within `paragraph`,
// `unordered-list-item` and `ordered-list-item` block types as well instead of
// only within `unstyled` block types. I also removed TypeScript type hints
// because I had to.
// Original license:
/*
MIT License

Copyright (c) 2021 Anton Fedorovskyi

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import PropTypes from 'prop-types';
import React from 'react';
import { RichUtils, Modifier, EditorState, SelectionState } from 'draft-js';
import linkifyIt from 'linkify-it';
import tlds from 'tlds';

const linkify = linkifyIt().tlds(tlds);

function getUrlFromString(text) {
  const matchLinkList = linkify.match(text);
  return matchLinkList && matchLinkList[0].url;
}

function isURL(text) {
  return !!linkify.match(text);
}

function editorStateSettingLink(editorState, selection, data) {
  const contentState = editorState.getCurrentContent();
  const entityKey = getCurrentLinkEntityKey(editorState);

  let nextEditorState = editorState;

  if (!entityKey) {
    const contentStateWithEntity = contentState.createEntity(
      'LINK',
      'MUTABLE',
      data
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    nextEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity
    });
    nextEditorState = RichUtils.toggleLink(
      nextEditorState,
      selection,
      entityKey
    );
  } else {
    nextEditorState = EditorState.set(editorState, {
      currentContent: editorState
        .getCurrentContent()
        .replaceEntityData(entityKey, data)
    });
    nextEditorState = EditorState.forceSelection(
      nextEditorState,
      editorState.getSelection()
    );
  }

  return nextEditorState;
}

function getCurrentLinkEntityKey(editorState) {
  const contentState = editorState.getCurrentContent();
  const startKey = editorState.getSelection().getStartKey();
  const startOffset = editorState.getSelection().getStartOffset();
  const block = contentState.getBlockForKey(startKey);

  const linkKey = block.getEntityAt(
    Math.min(block.getText().length - 1, startOffset)
  );

  if (linkKey) {
    const linkInstance = contentState.getEntity(linkKey);
    if (linkInstance.getType() === 'LINK') {
      return linkKey;
    }
  }
  return null;
}

const Link = props => {
  const data = props.contentState.getEntity(props.entityKey).getData();
  const { url } = data;
  if (!url) {
    return <span>{props.children}</span>;
  }
  return (
    <a href={url} title={url}>
      {props.children}
    </a>
  );
};

Link.propTypes = {
  contentState: PropTypes.object.isRequired,
  entityKey: PropTypes.string.isRequired
};

function findLinkEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges(character => {
    const entityKey = character.getEntity();
    if (!entityKey) return;
    const entity = contentState.getEntity(entityKey);
    return entity.getType() === 'LINK' && entity.getData().url;
  }, callback);
}

const blockTypesWithLinkDetection = [
  'unstyled',
  'paragraph',
  'unordered-list-item',
  'ordered-list-item'
];

const createLinkDetectionPlugin = () => {
  return {
    decorators: [
      {
        strategy: findLinkEntities,
        component: Link
      }
    ],
    onChange: editorState => {
      const contentState = editorState.getCurrentContent();

      const selection = editorState.getSelection();

      if (!selection || !selection.isCollapsed()) {
        return editorState;
      }

      const cursorOffset = selection.getStartOffset();
      const cursorBlockKey = selection.getStartKey();
      const cursorBlock = contentState.getBlockForKey(cursorBlockKey);

      if (!blockTypesWithLinkDetection.includes(cursorBlock.getType())) {
        return editorState;
      }

      // Step 1: Get the word around the cursor by splitting the current block's text
      const text = cursorBlock.getText();
      const currentWordStart = text.lastIndexOf(' ', cursorOffset) + 1;
      let currentWordEnd = text.indexOf(' ', cursorOffset);
      if (currentWordEnd === -1) {
        currentWordEnd = text.length;
      }

      const currentWord = text.substr(
        currentWordStart,
        currentWordEnd - currentWordStart
      );

      const currentWordIsURL = isURL(currentWord);

      // Step 2: Find the existing LINK entity under the user's cursor
      let currentLinkEntityKey = cursorBlock.getEntityAt(
        Math.min(text.length - 1, cursorOffset)
      );
      const inst =
        currentLinkEntityKey && contentState.getEntity(currentLinkEntityKey);
      if (inst && inst.getType() !== 'LINK') {
        currentLinkEntityKey = '';
      }

      if (currentLinkEntityKey) {
        // Note: we don't touch link values added / removed "explicitly" via the link
        // toolbar button. This means you can make a link with text that doesn't match the link.
        const entityExistingData = contentState
          .getEntity(currentLinkEntityKey)
          .getData();
        if (entityExistingData.explicit) {
          return editorState;
        }

        if (currentWordIsURL) {
          // We are modifying the URL - update the entity to reflect the current text
          const contentState = editorState.getCurrentContent();
          return EditorState.set(editorState, {
            currentContent: contentState.replaceEntityData(
              currentLinkEntityKey,
              {
                explicit: false,
                url: getUrlFromString(currentWord)
              }
            )
          });
        } else {
          // We are no longer in a URL but the entity is still present. Remove it from
          // the current character so the linkifying "ends".
          const entityRange = new SelectionState({
            anchorOffset: currentWordStart - 1,
            anchorKey: cursorBlockKey,
            focusOffset: currentWordStart,
            focusKey: cursorBlockKey,
            isBackward: false,
            hasFocus: true
          });
          return EditorState.set(editorState, {
            currentContent: Modifier.applyEntity(
              editorState.getCurrentContent(),
              entityRange,
              null
            )
          });
        }
      }

      // There is no entity beneath the current word, but it looks like a URL. Linkify it!
      if (!currentLinkEntityKey && currentWordIsURL) {
        const entityRange = new SelectionState({
          anchorOffset: currentWordStart,
          anchorKey: cursorBlockKey,
          focusOffset: currentWordEnd,
          focusKey: cursorBlockKey,
          isBackward: false,
          hasFocus: false
        });

        let newEditorState = editorStateSettingLink(editorState, entityRange, {
          explicit: false,
          url: getUrlFromString(currentWord)
        });

        // reset selection to the initial cursor to avoid selecting the entire links
        newEditorState = EditorState.acceptSelection(newEditorState, selection);
        return newEditorState;
      }

      return editorState;
    }
  };
};

export default createLinkDetectionPlugin;
